import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { abrirArchivo } from "../components/enrollment/visorArchivo";
import { push } from "../navigation/v2/sales/push";
import Redactar, { Borrador } from "./redactar";
import AjustesCorreo from "./ajustesCorreo";
import { refrescarNoLeidosCorreo } from "./noLeidos";
import { AdjuntoInfo, Carpeta, Dir, MAIL_URL, MiBuzon, Mensaje, Resumen, errorCorreo, fechaCorta, fechaLarga, mailApi, nombreDe, tamano } from "./wappmail.service";
import "../avisos/avisos.css";
import "./wappmail.css";

/**
 * WappMail (sección 82, Esteban 2026-10-10): el correo @washingtonacademy.com dentro de WAPP. Carpetas a la izquierda, lista de
 * mensajes, panel de lectura y ventana de redactar. Todo el correo lo hace wa-mail (IMAP/SMTP con Migadu); aquí solo se muestra.
 */

const CSP = (imagenes: boolean) => "default-src 'none'; img-src data: " + (MAIL_URL ? new URL(MAIL_URL, window.location.href).origin + " " : "") + (imagenes ? "https: http:" : "") + "; style-src 'unsafe-inline'; font-src data:";
const documento = (html: string, imagenes: boolean) => '<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="' + CSP(imagenes) + '">'
	+ '<base target="_blank"><style>html,body{margin:0}body{padding:14px 16px;background:#fff;color:#1d1d1f;font:14px/1.55 "Hanken Grotesk",Arial,Helvetica,sans-serif;overflow-wrap:anywhere}'
	+ 'img{max-width:100%;height:auto}table{max-width:100%}pre{white-space:pre-wrap}a{color:#2B62E0}blockquote{margin:0 0 0 6px;padding-left:10px;border-left:3px solid #E4DED8}</style></head><body>'
	+ html + "</body></html>";

const Cuerpo: React.FC<{ html: string; imagenes: boolean }> = ({ html, imagenes }) => {
	const ref = useRef<HTMLIFrameElement>(null);
	const obs = useRef<ResizeObserver | null>(null);
	const cuadro = useRef(0);
	// El alto del mensaje se mide desde fuera (el iframe no corre scripts). Con requestAnimationFrame y solo si cambió, para no
	// provocar el "ResizeObserver loop" cuando al cambiar el alto del iframe cambia el del contenido.
	const alto = () => {
		cancelAnimationFrame(cuadro.current);
		cuadro.current = requestAnimationFrame(() => {
			const f = ref.current;
			const h = Math.max(120, f?.contentDocument?.documentElement?.scrollHeight || 0);
			if (f && f.style.height !== h + "px") f.style.height = h + "px";
		});
	};
	const alCargar = () => {
		obs.current?.disconnect();
		alto();
		const d = ref.current?.contentDocument;
		if (d?.body && typeof ResizeObserver !== "undefined") { obs.current = new ResizeObserver(alto); obs.current.observe(d.body); }
		d?.querySelectorAll("img").forEach((i) => i.addEventListener("load", alto));
	};
	useEffect(() => () => { obs.current?.disconnect(); cancelAnimationFrame(cuadro.current); }, []);
	return <iframe ref={ref} className="wm-cuerpo" title="Mensaje" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={documento(html, imagenes)} onLoad={alCargar} />;
};

const Globo: React.FC<{ n: number | null | undefined }> = ({ n }) => (n && n > 0 ? <span className="wm-n">{n > 99 ? "99+" : n}</span> : null);

const WappMail: React.FC = () => {
	const [yo, setYo] = useState<MiBuzon | null>(null);
	const [estado, setEstado] = useState<"cargando" | "sin" | "listo" | "caido">("cargando");
	const [detalleSin, setDetalleSin] = useState<string | null>(null);
	const [carpetas, setCarpetas] = useState<Carpeta[]>([]);
	const [carpeta, setCarpeta] = useState("INBOX");
	const [lista, setLista] = useState<Resumen[]>([]);
	const [total, setTotal] = useState(0);
	const [pagina, setPagina] = useState(1);
	const [cargandoLista, setCargandoLista] = useState(false);
	const [q, setQ] = useState("");
	const [busca, setBusca] = useState("");
	const [uid, setUid] = useState<number | null>(null);
	const [msg, setMsg] = useState<Mensaje | null>(null);
	const [cargandoMsg, setCargandoMsg] = useState(false);
	const [imagenes, setImagenes] = useState(false);
	const [marcados, setMarcados] = useState<Set<number>>(new Set());
	const [borrador, setBorrador] = useState<Borrador | null>(null);
	const [ajustes, setAjustes] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [moverAbierto, setMoverAbierto] = useState(false);
	const claveBorrador = useRef(1);
	const listaRef = useRef<Resumen[]>([]);
	listaRef.current = lista;
	const carpetaRef = useRef(carpeta);
	carpetaRef.current = carpeta;
	const buscaRef = useRef(busca);
	buscaRef.current = busca;

	useEffect(() => { if (aviso) { const t = setTimeout(() => setAviso(null), 4000); return () => clearTimeout(t); } }, [aviso]);

	// ---------------------------------------------------------------- carga
	const cargarCarpetas = useCallback(() => mailApi.carpetas().then((r) => setCarpetas(r.data.carpetas)).catch(() => { /* se queda lo de antes */ }), []);

	const cargarLista = useCallback(async (ruta: string, pag: number, texto: string, anexar: boolean) => {
		setCargandoLista(true);
		try {
			const r = texto ? await mailApi.buscar(ruta, texto, pag) : await mailApi.mensajes(ruta, pag);
			if (ruta !== carpetaRef.current || texto !== buscaRef.current) return;
			setTotal(r.data.total);
			setPagina(pag);
			setLista((l) => (anexar ? [...l, ...r.data.mensajes.filter((m) => !l.some((x) => x.uid === m.uid))] : r.data.mensajes));
			setError(null);
		} catch (e) { setError(errorCorreo(e)); } finally { setCargandoLista(false); }
	}, []);

	const iniciar = useCallback(async () => {
		setEstado("cargando");
		try {
			const r = await mailApi.yo();
			setYo(r.data);
			if (!r.data.buzon) { setDetalleSin(null); setEstado("sin"); return; }
			if (r.data.buzon.estado !== "activo") { setDetalleSin("Tu buzón está suspendido. Pídele a Super Admin que lo reactive."); setEstado("sin"); return; }
			if (!r.data.buzon.con_clave) { setDetalleSin("Tu buzón aún no tiene contraseña de WAPP. Pídele a Super Admin que la renueve."); setEstado("sin"); return; }
			await Promise.all([cargarCarpetas(), cargarLista("INBOX", 1, "", false)]);
			setEstado("listo");
		} catch (e) { setError(errorCorreo(e)); setEstado("caido"); }
	}, [cargarCarpetas, cargarLista]);

	useEffect(() => { iniciar(); }, [iniciar]);

	// Correo nuevo: aviso al instante (wapp-push) y repaso cada minuto con la pestaña a la vista.
	const refrescar = useCallback(async () => {
		if (buscaRef.current) return;
		try {
			const [c, r] = await Promise.all([mailApi.carpetas(), mailApi.mensajes(carpetaRef.current, 1)]);
			setCarpetas(c.data.carpetas);
			setTotal(r.data.total);
			// Lo de arriba (página 1) se renueva; lo de más abajo, ya cargado con "Cargar Más", se conserva.
			setLista((l) => {
				const nuevos = r.data.mensajes;
				if (!nuevos.length) return [];
				const min = nuevos[nuevos.length - 1].uid;
				return [...nuevos, ...l.filter((m) => m.uid < min)];
			});
		} catch { /* sin red un momento */ }
	}, []);
	useEffect(() => {
		if (estado !== "listo") return;
		const quitar = push.on("correo", () => refrescar());
		const ciclo = setInterval(() => { if (document.visibilityState === "visible") refrescar(); }, 60000);
		return () => { quitar(); clearInterval(ciclo); };
	}, [estado, refrescar]);

	// ---------------------------------------------------------------- navegación
	const irA = (ruta: string) => {
		if (ruta === carpeta && !busca) return;
		setCarpeta(ruta); carpetaRef.current = ruta;
		setQ(""); setBusca(""); buscaRef.current = "";
		setUid(null); setMsg(null); setMarcados(new Set()); setLista([]); setTotal(0);
		cargarLista(ruta, 1, "", false);
	};
	const buscar = (e: React.FormEvent) => {
		e.preventDefault();
		const t = q.trim();
		if (t.length === 1) return;
		setBusca(t); buscaRef.current = t; setUid(null); setMsg(null); setLista([]); setTotal(0);
		cargarLista(carpeta, 1, t, false);
	};
	const limpiarBusqueda = () => { setQ(""); setBusca(""); buscaRef.current = ""; setLista([]); cargarLista(carpeta, 1, "", false); };

	const especial = (uso: string) => carpetas.find((c) => c.especial === uso)?.ruta || null;
	const esEnviados = carpetas.find((c) => c.ruta === carpeta)?.especial === "\\Sent" || carpetas.find((c) => c.ruta === carpeta)?.especial === "\\Drafts";

	const abrir = async (m: Resumen, mostrarImagenes = false) => {
		setUid(m.uid); setImagenes(mostrarImagenes); setCargandoMsg(true); setError(null);
		if (!mostrarImagenes) setMsg(null);
		try {
			const r = await mailApi.mensaje(carpeta, m.uid, mostrarImagenes);
			setMsg(r.data);
			if (!m.leido) {
				setLista((l) => l.map((x) => (x.uid === m.uid ? { ...x, leido: true } : x)));
				setCarpetas((c) => c.map((f) => (f.ruta === carpeta && f.no_leidos ? { ...f, no_leidos: f.no_leidos - 1 } : f)));
				refrescarNoLeidosCorreo();
			}
		} catch (e) { setError(errorCorreo(e)); setUid(null); } finally { setCargandoMsg(false); }
	};

	// ---------------------------------------------------------------- acciones
	const quitarDeLista = (uids: number[]) => {
		const s = new Set(uids);
		setLista((l) => l.filter((x) => !s.has(x.uid)));
		setTotal((t) => Math.max(0, t - uids.length));
		setMarcados(new Set());
		if (uid && s.has(uid)) { setUid(null); setMsg(null); }
	};
	const accion = async (fn: () => Promise<any>, okTexto?: string) => {
		setError(null);
		try { await fn(); if (okTexto) setAviso(okTexto); cargarCarpetas(); refrescarNoLeidosCorreo(); } catch (e) { setError(errorCorreo(e)); }
	};
	const marcarLeido = (uids: number[], leido: boolean) => accion(async () => {
		await mailApi.marcar(carpeta, uids, "leido", leido);
		const s = new Set(uids);
		setLista((l) => l.map((x) => (s.has(x.uid) ? { ...x, leido } : x)));
		if (!leido && uid && s.has(uid)) { setUid(null); setMsg(null); }
	});
	const destacar = (uids: number[], valor: boolean) => accion(async () => {
		await mailApi.marcar(carpeta, uids, "marcado", valor);
		const s = new Set(uids);
		setLista((l) => l.map((x) => (s.has(x.uid) ? { ...x, marcado: valor } : x)));
		setMsg((m) => (m && s.has(m.uid) ? { ...m, marcado: valor } : m));
	});
	const mover = (uids: number[], destino: string, nombre: string) => accion(async () => { await mailApi.mover(carpeta, uids, destino); quitarDeLista(uids); }, uids.length > 1 ? "Correos movidos a " + nombre + "." : "Correo movido a " + nombre + ".");
	const eliminar = (uids: number[]) => accion(async () => {
		const r = await mailApi.borrar(carpeta, uids);
		quitarDeLista(uids);
		setAviso(r.data.definitivo ? "Eliminado para siempre." : "Movido a la papelera.");
	});

	const abrirAdjunto = (a: AdjuntoInfo) => {
		if (!msg) return;
		const { carpeta: c, uid: u } = msg;
		abrirArchivo({
			nombre: a.nombre, mime: a.tipo, descargar: true,
			cargar: async () => ({ blob: (await mailApi.adjunto(c, u, a.parte)).data, nombre: a.nombre }),
		});
	};

	// ---------------------------------------------------------------- redactar
	const nuevoBorrador = (b: Partial<Borrador>) => setBorrador({ clave: claveBorrador.current++, para: [], cc: [], cco: [], asunto: "", html: "", ...b });
	const yoEmail = (yo?.buzon?.email || "").toLowerCase();
	const sinYo = (l: Dir[]) => l.filter((d) => d.email !== yoEmail);
	const unicos = (l: Dir[]) => l.filter((d, i) => l.findIndex((x) => x.email === d.email) === i);
	const prefijo = (p: string, a: string) => (new RegExp("^" + p + ":\\s*", "i").test(a) ? a : p + ": " + a);
	const responder = (todos: boolean) => {
		if (!msg) return;
		const destino = msg.responder_a[0] || msg.de;
		const mio = !!msg.de && msg.de.email === yoEmail;
		const para = mio ? msg.para : destino ? [destino] : [];
		const cc = todos ? unicos(sinYo([...(mio ? [] : msg.para), ...msg.cc]).filter((d) => !para.some((p) => p.email === d.email))) : [];
		nuevoBorrador({ para: unicos(sinYo(para)), cc, asunto: prefijo("Re", msg.asunto), responder: { carpeta: msg.carpeta, uid: msg.uid }, nota: "El mensaje original se incluye abajo al enviar." });
	};
	const reenviar = () => msg && nuevoBorrador({ asunto: prefijo("Fwd", msg.asunto), reenviar: { carpeta: msg.carpeta, uid: msg.uid }, nota: "El mensaje original y sus adjuntos se incluyen al enviar." });

	// ---------------------------------------------------------------- vistas
	const carpetaActual = carpetas.find((c) => c.ruta === carpeta);
	const destinos = useMemo(() => carpetas.filter((c) => c.ruta !== carpeta && c.especial !== "\\Flagged" && c.especial !== "\\All"), [carpetas, carpeta]);
	const hayMas = lista.length < total;
	const vistaMovil = uid ? "lector" : "lista";

	if (estado === "cargando") return <div className="avisos-pag wm-pag"><div className="av-vacio">Cargando tu correo…</div></div>;
	if (estado === "sin") {
		return (
			<div className="avisos-pag wm-pag">
				<div className="av-vacio wm-sin">
					<h1>{detalleSin ? "Tu correo no está listo" : "Tu correo se activa en la mudanza"}</h1>
					<p>{detalleSin || "Cuando esté listo, vas a leer y mandar tu correo de @washingtonacademy.com aquí."}</p>
				</div>
			</div>
		);
	}
	if (estado === "caido") {
		return (
			<div className="avisos-pag wm-pag">
				<div className="av-vacio wm-sin">
					<h1>No se pudo abrir tu correo</h1>
					<p className="co-oro">{error}</p>
					<button type="button" className="av-btn pri" onClick={iniciar}>Reintentar</button>
				</div>
			</div>
		);
	}

	const archivo = especial("\\Archive");
	const papelera = especial("\\Trash");

	return (
		<div className={"avisos-pag wm-pag wm-vista-" + vistaMovil}>
			<div className="wm-cab">
				<h1>WappMail</h1>
				<span className="av-tenue wm-yo">{yo?.buzon?.email}</span>
				<form className="wm-buscar" onSubmit={buscar} role="search">
					<input className="av-buscar" placeholder="Buscar en esta carpeta" aria-label="Buscar correo" value={q} onChange={(e) => setQ(e.target.value)} />
					{busca ? <button type="button" className="av-link" onClick={limpiarBusqueda}>Quitar Búsqueda</button> : null}
				</form>
				<button type="button" className="av-btn pri" onClick={() => nuevoBorrador({})}>Redactar</button>
				<button type="button" className="av-btn" onClick={() => setAjustes(true)}>Ajustes</button>
			</div>

			{error ? <div className="wm-aviso oro" role="alert" onClick={() => setError(null)}>{error}</div> : null}
			{aviso ? <div className="wm-aviso ok" role="status">{aviso}</div> : null}

			<div className="wm-marco">
				<nav className="wm-carpetas" aria-label="Carpetas">
					{carpetas.map((c) => (
						<button key={c.ruta} type="button" className={"wm-carpeta" + (c.ruta === carpeta ? " on" : "")} onClick={() => irA(c.ruta)} aria-current={c.ruta === carpeta}>
							<span>{c.nombre}</span>
							{c.especial === "\\Inbox" || c.especial === "\\Drafts" || !c.especial ? <Globo n={c.no_leidos} /> : c.no_leidos ? <small>{c.no_leidos}</small> : null}
						</button>
					))}
				</nav>

				<section className="wm-lista" aria-label="Mensajes">
					<div className="wm-lista-cab">
						<label className="av-check" title="Elegir todos">
							<input type="checkbox" checked={lista.length > 0 && marcados.size === lista.length} onChange={(e) => setMarcados(e.target.checked ? new Set(lista.map((m) => m.uid)) : new Set())} />
						</label>
						<b>{busca ? "Resultados" : carpetaActual?.nombre}</b>
						<span className="av-tenue">{total ? total + (total === 1 ? " correo" : " correos") : ""}</span>
						<button type="button" className="av-link wm-refrescar" onClick={() => { cargarCarpetas(); cargarLista(carpeta, 1, busca, false); }}>Actualizar</button>
					</div>
					{marcados.size ? (
						<div className="wm-masivo">
							<span>{marcados.size} elegidos</span>
							<button type="button" className="av-btn chico" onClick={() => marcarLeido(Array.from(marcados), true)}>Marcar Leídos</button>
							<button type="button" className="av-btn chico" onClick={() => marcarLeido(Array.from(marcados), false)}>Marcar No Leídos</button>
							{archivo && carpeta !== archivo ? <button type="button" className="av-btn chico" onClick={() => mover(Array.from(marcados), archivo, "Archivo")}>Archivar</button> : null}
							<button type="button" className="av-btn chico" onClick={() => eliminar(Array.from(marcados))}>Eliminar</button>
						</div>
					) : null}
					<ul className="wm-filas">
						{lista.map((m) => (
							<li key={m.uid} className={"wm-fila" + (m.leido ? "" : " nuevo") + (m.uid === uid ? " on" : "")}>
								<label className="av-check wm-chk"><input type="checkbox" aria-label="Elegir" checked={marcados.has(m.uid)}
									onChange={(e) => setMarcados((s) => { const n = new Set(s); if (e.target.checked) n.add(m.uid); else n.delete(m.uid); return n; })} /></label>
								<button type="button" className={"wm-estrella" + (m.marcado ? " on" : "")} aria-label={m.marcado ? "Quitar destacado" : "Destacar"} aria-pressed={m.marcado}
									onClick={() => destacar([m.uid], !m.marcado)}>{m.marcado ? "★" : "☆"}</button>
								<button type="button" className="wm-fila-cuerpo" onClick={() => abrir(m)}>
									<span className="wm-l1">
										<span className="wm-de-l">{esEnviados ? "Para: " + (m.para.map(nombreDe).join(", ") || "(sin destinatario)") : nombreDe(m.de)}</span>
										<small>{fechaCorta(m.fecha)}</small>
									</span>
									<span className="wm-l2">
										{!m.leido ? <i className="wm-punto" aria-label="Sin leer" /> : null}
										<span className="wm-asu">{m.asunto || "(sin asunto)"}</span>
										{m.adjuntos ? <span className="wm-clip" aria-label="Con adjuntos" title="Con adjuntos">📎</span> : null}
										{m.respondido ? <span className="wm-clip" title="Respondido">↩</span> : null}
									</span>
								</button>
							</li>
						))}
						{!lista.length && !cargandoLista ? <li className="av-vacio">{busca ? "Sin resultados." : "Esta carpeta está vacía."}</li> : null}
						{cargandoLista ? <li className="av-vacio">Cargando…</li> : null}
						{hayMas && !cargandoLista ? <li className="wm-mas"><button type="button" className="av-btn" onClick={() => cargarLista(carpeta, pagina + 1, busca, true)}>Cargar Más</button></li> : null}
					</ul>
				</section>

				<section className="wm-lector" aria-label="Lectura">
					{!uid ? <div className="av-vacio wm-nada">Elige un correo para leerlo.</div> : (
						<>
							<div className="wm-acciones">
								<button type="button" className="av-btn chico wm-atras" onClick={() => { setUid(null); setMsg(null); }}>← Volver</button>
								<button type="button" className="av-btn chico" disabled={!msg} onClick={() => responder(false)}>Responder</button>
								<button type="button" className="av-btn chico" disabled={!msg} onClick={() => responder(true)}>Responder a Todos</button>
								<button type="button" className="av-btn chico" disabled={!msg} onClick={reenviar}>Reenviar</button>
								{archivo && carpeta !== archivo ? <button type="button" className="av-btn chico" onClick={() => mover([uid], archivo, "Archivo")}>Archivar</button> : null}
								<button type="button" className="av-btn chico" onClick={() => eliminar([uid])}>{carpeta === papelera ? "Eliminar Para Siempre" : "Eliminar"}</button>
								<button type="button" className="av-btn chico" onClick={() => marcarLeido([uid], false)}>Marcar No Leído</button>
								{msg ? <button type="button" className="av-btn chico" onClick={() => destacar([uid], !msg.marcado)}>{msg.marcado ? "Quitar Destacado" : "Destacar"}</button> : null}
								<span className="wm-mover">
									<button type="button" className="av-btn chico" aria-expanded={moverAbierto} onClick={() => setMoverAbierto((v) => !v)}>Mover A…</button>
									{moverAbierto ? (
										<ul className="wm-menu" role="menu">
											{destinos.map((d) => <li key={d.ruta}><button type="button" role="menuitem" onClick={() => { setMoverAbierto(false); mover([uid], d.ruta, d.nombre); }}>{d.nombre}</button></li>)}
										</ul>
									) : null}
								</span>
							</div>
							{cargandoMsg && !msg ? <div className="av-vacio">Abriendo…</div> : null}
							{msg ? (
								<article className="wm-msg">
									<h2>{msg.asunto || "(sin asunto)"}</h2>
									<div className="wm-meta">
										<div><b>{nombreDe(msg.de)}</b> <span className="av-tenue">{msg.de ? "<" + msg.de.email + ">" : ""}</span></div>
										<div className="av-tenue">Para: {msg.para.map((d) => d.nombre || d.email).join(", ") || "—"}{msg.cc.length ? " · Cc: " + msg.cc.map((d) => d.nombre || d.email).join(", ") : ""}</div>
										<div className="av-tenue">{fechaLarga(msg.fecha)}</div>
									</div>
									{msg.imagenes_bloqueadas > 0 && !imagenes ? (
										<div className="wm-aviso suave">Se bloquearon las imágenes de este correo.
											<button type="button" className="av-link" onClick={() => abrir({ uid: msg.uid, leido: true } as Resumen, true)}>Mostrar Imágenes</button></div>
									) : null}
									<Cuerpo html={msg.html} imagenes={imagenes} />
									{msg.adjuntos.length ? (
										<ul className="wm-adjuntos" aria-label="Adjuntos">
											{msg.adjuntos.map((a) => (
												<li key={a.parte}><button type="button" onClick={() => abrirAdjunto(a)} title={"Abrir " + a.nombre}><span className="wm-adj-n">{a.nombre}</span><small>{tamano(a.tam)}</small></button></li>
											))}
										</ul>
									) : null}
								</article>
							) : null}
						</>
					)}
				</section>
			</div>

			{ajustes ? <AjustesCorreo carpetas={carpetas.map((c) => c.ruta)} alCerrar={() => setAjustes(false)} /> : null}
			{borrador && yo ? (
				<Redactar key={borrador.clave} borrador={borrador} yo={yo}
					onCerrar={() => setBorrador(null)}
					onEnviado={(t) => { setBorrador(null); setAviso(t); cargarCarpetas(); if (esEnviados) refrescar(); }} />
			) : null}
		</div>
	);
};

export default WappMail;
